import type {
  CustomerProfile,
  Dashboard,
  Merchant,
  Order,
  PlatformOverview,
  PlatformSecurityCenter,
  Product,
  ProductSummary,
  SellerApplication
} from "../types";

const SELLER_ADMIN_TOKEN = "demo-admin-token";
const PLATFORM_ADMIN_TOKEN = "demo-platform-token";
const PLATFORM_SESSION_KEY = "lao_platform_admin_token";
const SELLER_SESSION_KEY = "lao_seller_admin_token";

const adminHeaders = (scope: "seller" | "platform" = "seller") => ({
  "x-admin-token":
    scope === "platform"
      ? window.localStorage.getItem(PLATFORM_SESSION_KEY) || PLATFORM_ADMIN_TOKEN
      : window.localStorage.getItem(SELLER_SESSION_KEY) || SELLER_ADMIN_TOKEN
});

export function storeAdminSession(scope: "seller" | "platform", token: string) {
  window.localStorage.setItem(scope === "platform" ? PLATFORM_SESSION_KEY : SELLER_SESSION_KEY, token);
}

export function clearAdminSession(scope: "seller" | "platform") {
  window.localStorage.removeItem(scope === "platform" ? PLATFORM_SESSION_KEY : SELLER_SESSION_KEY);
}

export async function startVerification(payload: {
  purpose: "platform_login" | "seller_registration" | "customer_registration";
  email?: string;
  phone?: string;
}): Promise<{ verificationId: string; expiresInSeconds: number; delivery: { email: boolean; phone: boolean }; demoCode?: string }> {
  const response = await fetch("/api/auth/verification/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Verification failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function confirmVerification(payload: {
  purpose: "platform_login" | "seller_registration" | "customer_registration";
  email?: string;
  phone?: string;
  code: string;
}): Promise<{
  verified: boolean;
  token?: string;
  expiresInSeconds?: number;
  actor?: { id: string; name: string; role: string; email?: string; phone?: string };
}> {
  const response = await fetch("/api/auth/verification/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Verification confirmation failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function fetchProduct(slug = "iphone-16-pro-max"): Promise<Product> {
  const response = await fetch(`/api/products/${slug}`);
  if (!response.ok) throw new Error("Unable to load product");
  return response.json();
}

export async function fetchProducts(): Promise<ProductSummary[]> {
  const response = await fetch("/api/products");
  if (!response.ok) throw new Error("Unable to load products");
  return response.json();
}

export async function fetchAdminProducts(): Promise<Product[]> {
  const response = await fetch("/api/admin/products", {
    headers: adminHeaders("seller")
  });
  if (!response.ok) throw new Error("Unable to load admin products");
  return response.json();
}

export async function fetchCustomerProfile(customerId = "cust_01"): Promise<CustomerProfile> {
  const response = await fetch(`/api/customer/profile?customerId=${encodeURIComponent(customerId)}`);
  if (!response.ok) throw new Error("Unable to load customer profile");
  return response.json();
}

export async function registerCustomerProfile(payload: {
  name: string;
  phone: string;
  email: string;
  address: string;
  bankAccount: string;
}): Promise<CustomerProfile> {
  const response = await fetch("/api/customer/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Customer registration failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function submitSellerApplication(payload: {
  storeName: string;
  ownerName: string;
  phone: string;
  email: string;
  address: string;
  businessType: string;
  category: string;
  level: string;
  facebook: string;
  messenger: string;
  whatsapp: string;
  bankAccount: string;
  intro: string;
  qrFile?: File | null;
}): Promise<SellerApplication> {
  const body = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (key === "qrFile") return;
    body.append(key, String(value ?? ""));
  });
  if (payload.qrFile) body.append("qr", payload.qrFile);
  const response = await fetch("/api/seller-applications", {
    method: "POST",
    body
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Seller application failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function createOrder(payload: {
  slug: string;
  skuId: string;
  customerName: string;
  phone: string;
  address: string;
  quantity: number;
  channel: string;
  shareId?: string;
  clientRequestId: string;
}): Promise<Order> {
  const response = await fetch("/api/orders", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Order failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function lookupOrder(orderNumber: string, phoneSuffix: string): Promise<Order> {
  const response = await fetch(`/api/orders/${encodeURIComponent(orderNumber)}?phoneSuffix=${encodeURIComponent(phoneSuffix)}`);
  if (!response.ok) throw new Error("没有找到订单,请检查订单号和手机后四位。");
  return response.json();
}

export async function uploadPaymentProof(
  orderNumber: string,
  phoneSuffix: string,
  file: File,
  aiDetection?: {
    detectedMerchantName?: string;
    detectedAccount?: string;
    detectedAmount?: string;
  }
): Promise<Order> {
  const body = new FormData();
  body.append("phoneSuffix", phoneSuffix);
  body.append("proof", file);
  if (aiDetection?.detectedMerchantName) body.append("detectedMerchantName", aiDetection.detectedMerchantName);
  if (aiDetection?.detectedAccount) body.append("detectedAccount", aiDetection.detectedAccount);
  if (aiDetection?.detectedAmount) body.append("detectedAmount", aiDetection.detectedAmount);
  const response = await fetch(`/api/orders/${encodeURIComponent(orderNumber)}/payment-proof`, {
    method: "POST",
    body
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Payment proof upload failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function confirmOrderPayment(orderId: string): Promise<Order> {
  const response = await fetch(`/api/admin/orders/${orderId}/payment-confirm`, {
    method: "POST",
    headers: adminHeaders("seller")
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Confirm payment failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function uploadOrderLogisticsQr(orderId: string, file: File): Promise<Order> {
  const body = new FormData();
  body.append("qr", file);
  const response = await fetch(`/api/admin/orders/${orderId}/logistics-qr`, {
    method: "POST",
    headers: adminHeaders("seller"),
    body
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Upload logistics QR failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function completeOrderLogistics(orderId: string): Promise<Order> {
  const response = await fetch(`/api/admin/orders/${orderId}/logistics-complete`, {
    method: "POST",
    headers: adminHeaders("seller")
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Complete logistics failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function track(eventName: string, channel: string, metadata: Record<string, unknown> = {}) {
  await fetch("/api/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      merchantId: "m_yourbrand",
      productId: "prod_iphone16pm",
      channel,
      eventName,
      shareId: new URLSearchParams(window.location.search).get("share_id"),
      metadata
    })
  }).catch(() => undefined);
}

export async function fetchDashboard(): Promise<Dashboard> {
  const response = await fetch("/api/admin/dashboard", {
    headers: adminHeaders("seller")
  });
  if (!response.ok) throw new Error("Unable to load dashboard");
  return response.json();
}

export async function fetchPlatformOverview(): Promise<PlatformOverview> {
  const response = await fetch("/api/admin/platform", {
    headers: adminHeaders("platform")
  });
  if (!response.ok) throw new Error("Unable to load platform overview");
  return response.json();
}

export async function fetchPlatformSecurity(): Promise<PlatformSecurityCenter> {
  const response = await fetch("/api/admin/security", {
    headers: adminHeaders("platform")
  });
  if (!response.ok) throw new Error("Unable to load platform security center");
  return response.json();
}

export async function reviewSellerApplication(applicationId: string, decision: "approved" | "rejected", notes = ""): Promise<PlatformOverview> {
  const response = await fetch(`/api/admin/seller-applications/${applicationId}/review`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...adminHeaders("platform")
    },
    body: JSON.stringify({ decision, notes })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Review seller application failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function createAdminProduct(payload: {
  title: string;
  subtitle: string;
  slug?: string;
  price: number;
  compareAt: number;
  categoryId: string;
  description?: string;
  specs?: string;
}): Promise<Product> {
  const response = await fetch("/api/admin/products", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...adminHeaders("seller")
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Create product failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function updateAdminProduct(
  productId: string,
  payload: {
    title: string;
    subtitle: string;
    slug: string;
    price: number;
    compareAt: number;
    categoryId: string;
    stockStatus: string;
    status: string;
    seoTitle?: string;
    seoDescription?: string;
    specs?: string;
    featureTitle?: string;
    featureBody?: string;
    skus: Array<{
      id?: string;
      color: string;
      colorHex: string;
      storage: string;
      sku: string;
      priceDelta: number;
      stockQuantity: number;
    }>;
  }
): Promise<Product> {
  const response = await fetch(`/api/admin/products/${productId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...adminHeaders("seller")
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Update product failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function uploadProductVideo(productId: string, file: File, durationSeconds: number): Promise<Product> {
  const body = new FormData();
  body.append("video", file);
  body.append("durationSeconds", String(durationSeconds));
  body.append("label", "产品视频");

  const response = await fetch(`/api/admin/products/${productId}/video`, {
    method: "POST",
    headers: adminHeaders("seller"),
    body
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Upload video failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function uploadProductImages(productId: string, files: File[]): Promise<Product> {
  const body = new FormData();
  files.forEach((file) => body.append("images", file));

  const response = await fetch(`/api/admin/products/${productId}/images`, {
    method: "POST",
    headers: adminHeaders("seller"),
    body
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Upload images failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function updateMerchantProfile(merchantId: string, payload: Partial<Merchant>): Promise<Merchant> {
  const response = await fetch(`/api/admin/merchants/${merchantId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...adminHeaders("seller")
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Update merchant failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function uploadMerchantQr(merchantId: string, file: File): Promise<Merchant> {
  const body = new FormData();
  body.append("qr", file);

  const response = await fetch(`/api/admin/merchants/${merchantId}/payment-qr`, {
    method: "POST",
    headers: adminHeaders("seller"),
    body
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Upload payment QR failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function submitReview(
  slug: string,
  payload: {
    customerName: string;
    rating: number;
    channel: string;
    body: string;
    orderNumber?: string;
    phoneSuffix?: string;
  },
  images: File[],
  videos: File[] = []
): Promise<{ product: Product; reviewId: string; status: string; verified: boolean }> {
  const body = new FormData();
  body.append("customerName", payload.customerName);
  body.append("rating", String(payload.rating));
  body.append("channel", payload.channel);
  body.append("body", payload.body);
  if (payload.orderNumber) body.append("orderNumber", payload.orderNumber);
  if (payload.phoneSuffix) body.append("phoneSuffix", payload.phoneSuffix);
  images.forEach((file) => body.append("images", file));
  videos.forEach((file) => body.append("videos", file));

  const response = await fetch(`/api/products/${slug}/reviews`, {
    method: "POST",
    body
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Submit review failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export async function markReviewHelpful(reviewId: string): Promise<Product> {
  let voterKey = window.localStorage.getItem("review_voter_key");
  if (!voterKey) {
    voterKey = crypto.randomUUID();
    window.localStorage.setItem("review_voter_key", voterKey);
  }
  const response = await fetch(`/api/reviews/${reviewId}/helpful`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ voterKey })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Helpful vote failed" }));
    throw new Error(error.error);
  }
  return response.json();
}

export function formatMoney(amount: number, currency = "THB") {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(amount / 100);
}
